import { useNavigate } from 'react-router-dom'
import { Badge, StatusBadge } from '../components/Badge'
import { useAuth } from '../components/AuthProvider'
import { hasRole } from '../components/AppShell'
import { useDraftGuard } from '../components/DraftGuard'
import { Icon } from '../components/Icon'
import { PageHeader } from '../components/PageHeader'

const activity = [
  { icon: 'check' as const, tint: 'mint', title: 'Expense approved', detail: 'Metro Rail · $36.00', time: 'Yesterday' },
  { icon: 'arrow-right' as const, tint: 'blue', title: 'Timesheet submitted', detail: 'Aug 31 – Sep 6 · 39.5 hours', time: 'Sep 7' },
  { icon: 'warning' as const, tint: 'amber', title: 'Expense returned', detail: 'Aster Coffee · needs a business purpose', time: 'Sep 4' }
]

export const DashboardPage = () => {
  const { session } = useAuth()
  const { confirmNavigation } = useDraftGuard()
  const navigate = useNavigate()
  const isManager = hasRole(session?.user.roles || [], 'Manager')
  const isAdmin = hasRole(session?.user.roles || [], 'Admin')
  const firstName = session?.user.name.split(' ')[0] || 'there'

  const go = (path: string) => {
    if (confirmNavigation()) navigate(path)
  }

  return (
    <div className="page dashboard-page">
      <PageHeader
        eyebrow="MONDAY, SEPTEMBER 9"
        title={`Good morning, ${firstName}.`}
        description="Here’s the work that needs your attention today."
        actions={<button className="button button-primary" onClick={() => go('/time')}><Icon name="plus" size={17} /> Add time</button>}
      />

      <section className="metric-grid" aria-label="Your weekly overview">
        <article className="metric-card metric-card-primary">
          <div className="metric-card-header"><span className="metric-icon"><Icon name="clock" size={19} /></span><Badge tone="accent">This week</Badge></div>
          <div><strong>34.5<span>h</span></strong><p>of 40 scheduled hours</p></div>
          <div className="progress-track"><span style={{ width: '86%' }} /></div>
          <footer><span>5.5 h to complete</span><button onClick={() => go('/time')}>Open time <Icon name="arrow-right" size={15} /></button></footer>
        </article>
        <article className="metric-card">
          <div className="metric-card-header"><span className="metric-icon metric-icon-mint"><Icon name="receipt" size={19} /></span><Badge tone="warning">1 action</Badge></div>
          <div><strong>$481<span>.35</span></strong><p>September expenses submitted</p></div>
          <div className="metric-detail"><span className="dot dot-warning" /> One item was returned for clarification</div>
          <footer><span>3 expenses this month</span><button onClick={() => go('/expenses')}>View expenses <Icon name="arrow-right" size={15} /></button></footer>
        </article>
        {isManager && (
          <article className="metric-card metric-card-manager">
            <div className="metric-card-header"><span className="metric-icon metric-icon-purple"><Icon name="team" size={19} /></span><Badge tone="danger">Needs review</Badge></div>
            <div><strong>4</strong><p>submissions waiting for you</p></div>
            <div className="metric-avatars"><span className="avatar avatar-gold">PS</span><span className="avatar avatar-violet">MC</span><span className="avatar avatar-slate">NW</span><span className="avatar avatar-count">+1</span></div>
            <footer><span>Oldest is 2 days</span><button onClick={() => go('/approvals')}>Review queue <Icon name="arrow-right" size={15} /></button></footer>
          </article>
        )}
      </section>

      <section className="dashboard-layout">
        <article className="panel upcoming-panel">
          <div className="panel-heading"><div><p className="eyebrow">UP NEXT</p><h2>Keep your week moving</h2></div><button className="text-button" onClick={() => go('/time')}>Open timesheet <Icon name="arrow-right" size={15} /></button></div>
          <div className="action-list">
            <button className="action-item" onClick={() => go('/time')}>
              <span className="action-check"><Icon name="clock" size={16} /></span>
              <span><strong>Complete this week’s time</strong><small>5.5 hours remain before Friday</small></span>
              <span className="action-arrow"><Icon name="chevron-right" size={18} /></span>
            </button>
            <button className="action-item" onClick={() => go('/expenses')}>
              <span className="action-check action-check-warning"><Icon name="warning" size={16} /></span>
              <span><strong>Clarify Aster Coffee expense</strong><small>Add a business purpose, then resubmit</small></span>
              <span className="action-arrow"><Icon name="chevron-right" size={18} /></span>
            </button>
            <button className="action-item" onClick={() => go('/expenses')}>
              <span className="action-check action-check-muted"><Icon name="plus" size={16} /></span>
              <span><strong>Capture a new expense</strong><small>Receipt ready? Add it while details are fresh</small></span>
              <span className="action-arrow"><Icon name="chevron-right" size={18} /></span>
            </button>
          </div>
        </article>

        <article className="panel activity-panel">
          <div className="panel-heading"><div><p className="eyebrow">RECENT ACTIVITY</p><h2>Clear and traceable</h2></div><button className="icon-button" aria-label="More activity"><Icon name="more" size={18} /></button></div>
          <ol className="activity-list">
            {activity.map((item) => (
              <li key={item.title}>
                <span className={`activity-icon activity-${item.tint}`}><Icon name={item.icon} size={15} /></span>
                <span><strong>{item.title}</strong><small>{item.detail}</small></span>
                <time>{item.time}</time>
              </li>
            ))}
          </ol>
          <button className="full-text-button" onClick={() => go('/expenses')}>View all activity <Icon name="arrow-right" size={15} /></button>
        </article>
      </section>

      {isManager && (
        <section className="manager-spotlight">
          <div className="manager-spotlight-copy"><span className="spotlight-icon"><Icon name="team" size={20} /></span><div><p className="eyebrow">MANAGER DESK</p><h2>Four decisions can unblock your team.</h2><p>Two expense reports carry receipt-analysis signals. Your judgment always makes the final decision.</p></div></div>
          <div className="manager-spotlight-actions"><StatusBadge status="submitted" /><button className="button button-dark" onClick={() => go('/approvals')}>Open approvals <Icon name="arrow-right" size={17} /></button></div>
        </section>
      )}

      {isAdmin && <p className="admin-hint"><Icon name="shield" size={15} /> You have administrator access. Configuration changes are versioned and audit logged.</p>}
    </div>
  )
}
